import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CloseButton from 'components/shared/CloseButton';
import DataTable from 'components/shared/DataTable';
import DiscardChangesModal from 'components/shared/DiscardChangesModal';
import Foldout from 'components/shared/Foldout';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import Search from 'components/shared/Search';
import { useProfile } from 'context/profile';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { simpleDate, simpleDateAtTime } from 'lib/date';
import { Location } from 'models/flexible/Location';
import { Paginated } from 'models/Paginated';
import { PreferredProviderEntry, PreferredProviderListRow } from 'models/PreferredProviderList';
import { useInfiniteAdminGroups } from 'services/api/admin/groups';
import { adminLocationsQueryKeys, adminUpdatePreferredProviderList } from 'services/api/admin/locations';
import type { AdminLocationShowFields } from 'services/api/fields/admin/location';
import { groupsQueryKeys, useInfiniteGroups } from 'services/api/group';
import { preferredProviderListQueryKeys, updatePreferredProviderList } from 'services/api/preferredProviderLists';
import { validationErrorMessage } from 'services/api/validationErrors';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import TrashIcon from 'svg/TrashIcon';

type Props = {
  row: PreferredProviderListRow;
  // The client whose lists these are, and the provider types they can hold (selectableProviderTypesFor).
  // The customer page passes the acting client; Admin Locations passes the location's client.
  clientId: string;
  selectableTypeIds: string[];
  canEdit: boolean;
  // Admin Locations: reads and saves through the admin API, for a client the admin isn't acting as.
  admin?: boolean;
  // The admin panel opens before the list and client have loaded.
  loading?: boolean;
  handleClose: () => void;
};

const columnHelper = createColumnHelper<PreferredProviderEntry>();

// What the add dropdown reads from an option. /groups returns the Group model and /admin/groups a flexible
// Group, and both carry these.
type ProviderOption = { id: string; name: string; groupType?: { displayName?: string | null } | null };

const groupIdKey = (entries: PreferredProviderEntry[]) =>
  entries
    .map((entry) => entry.group.id)
    .sort()
    .join(',');

export default function PreferredProvidersSlideout({
  row,
  clientId,
  selectableTypeIds,
  canEdit,
  admin = false,
  loading = false,
  handleClose,
}: Props) {
  const { locationId, locationName, updatedAt, updatedByName, preferredProviders: saved } = row;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Adds and removes stay local until Apply; null means nothing has been touched yet.
  const [draft, setDraft] = useState<PreferredProviderEntry[] | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const invalidateQueries = useInvalidateQueries();
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();
  const { profile } = useProfile();

  useEffect(() => {
    requestAnimationFrame(() => setOpen(true));
  }, []);

  // Unmount once Foldout's 0.5s slide finishes. Unmounting earlier cuts the eased slide off near the end.
  const animateClose = () => {
    setOpen(false);
    setTimeout(handleClose, 500);
  };

  const rows = draft ?? saved;
  const hasChanges = draft !== null && groupIdKey(draft) !== groupIdKey(saved);

  // While the panel slides out after Apply, the draft still shows what was saved, so ignore another close.
  const requestClose = () => {
    if (!open) return;

    if (hasChanges) setConfirmDiscard(true);
    else animateClose();
  };

  // The groups a list can hold: the client's network groups of its selectable types, queried the same way
  // as the page's Filters drawer. Everything on the draft goes in id.not, so a provider removed in the
  // draft can be added back before Apply. The API's PreferredProvider::Selectable validates the save.
  // Both queries only fetch once the dropdown opens, so the unused one costs nothing.
  const optionParams = {
    networkClientId: clientId,
    // Ids, like the Filters drawer: `type` would make Group::Index load every group of those types first,
    // and /admin/groups doesn't split a comma-joined `type`.
    groupType: selectableTypeIds,
    'id.not': rows.map((entry) => entry.group.id).join(',') || undefined,
    sortBy: 'name asc',
  };
  // viewOnly so provider-only users get the client's whole network.
  const customerOptions = useInfiniteGroups({ ...optionParams, include: 'groupType', viewOnly: true });
  const adminOptions = useInfiniteAdminGroups(optionParams, ['groupType']);
  const asyncOptions = admin ? adminOptions : customerOptions;
  const editable = canEdit && !loading;

  // A saved provider removed and added back before Apply keeps its original "Added On".
  const addProvider = (group: ProviderOption) =>
    setDraft([
      ...rows,
      saved.find((entry) => entry.group.id === group.id) ?? {
        id: `new-${group.id}`,
        group: { id: group.id, name: group.name, groupType: { displayName: group.groupType?.displayName ?? null } },
        createdAt: new Date().toISOString(),
        // Same rule as the API: the customer page shows an admin as "Olio Admin", even to the admin, so they see
        // what the customer will. Admin Locations shows the name.
        createdByName: profile.isAdmin && !admin ? 'Olio Admin' : profile.fullName,
      },
    ]);

  const removeProvider = useCallback(
    (entry: PreferredProviderEntry) => setDraft(rows.filter((row) => row.group.id !== entry.group.id)),
    [rows]
  );

  const { mutate: apply, isPending: isApplying } = useMutateWithToast({
    mutationFn: (groupIds: string[]) =>
      admin
        ? adminUpdatePreferredProviderList(locationId, groupIds)
        : updatePreferredProviderList(locationId, groupIds),
    onSuccess: (savedRow: PreferredProviderListRow) => {
      if (admin) {
        // Like the customer branch: write the saved list into the panel's cached location, so reopening it before
        // the refetch lands shows the saved list rather than an editable pre-save one. A new Location, since
        // spreading the model into a plain object would drop its getters.
        queryClient.setQueryData<Location<AdminLocationShowFields<'preferredProviderList'>>>(
          adminLocationsQueryKeys.preferredProviderList(locationId),
          (location) => location && new Location({ ...location, preferredProviderList: savedRow })
        );
        queryClient.invalidateQueries({ queryKey: adminLocationsQueryKeys.show(locationId) });
      } else {
        // The PUT responds with the location's page row. Write it into the cached pages so reopening the
        // location before the refetch lands shows the saved list, not the one it opened with.
        queryClient.setQueriesData<Paginated<PreferredProviderListRow>>(
          { queryKey: preferredProviderListQueryKeys.base },
          (page) =>
            page && {
              ...page,
              data: page.data.map((row) => (row.locationId === savedRow.locationId ? savedRow : row)),
            }
        );
        invalidateQueries({ preferredProviderLists: true });
      }
      // The provider pickers sort by these lists. Reset, not invalidate: a picker that loads on menu open is
      // enabled: false and only fetches while unfetched, so an invalidate would leave its old order (see useWorkflow).
      queryClient.resetQueries({ queryKey: groupsQueryKeys.index });
      addToast({ text: 'Preferred providers successfully updated' });
      animateClose();
    },
    // The API's 422 explains why (a provider that can't be preferred here); anything else gets the generic toast.
    errorToast: validationErrorMessage,
  });

  const entries = useMemo(() => {
    const term = search.trim().toLowerCase();

    return term ? rows.filter((entry) => entry.group.name.toLowerCase().includes(term)) : rows;
  }, [rows, search]);

  const columns = useMemo(
    () => [
      columnHelper.accessor((entry) => entry.group.name, { id: 'name', header: 'Preferred Provider' }),
      columnHelper.accessor((entry) => entry.group.groupType.displayName, {
        id: 'type',
        header: 'Type',
        size: 110,
        enableSorting: false,
      }),
      columnHelper.accessor('createdAt', {
        header: 'Added On',
        enableSorting: false,
        // The trash icon shares this column so the three text columns stay equal widths.
        cell: (info) => (
          <AddedOnCell>
            {`${simpleDate(info.getValue())} by ${info.row.original.createdByName}`}
            {editable && (
              <IconButton
                aria-label={`Remove ${info.row.original.group.name}`}
                onClick={() => removeProvider(info.row.original)}>
                <TrashIcon width={14} height={14} color={colors.red100} />
              </IconButton>
            )}
          </AddedOnCell>
        ),
      }),
    ],
    [editable, removeProvider]
  );

  const count = rows.length;

  return (
    <>
      <Foldout open={open} position='right' onBackdropClick={requestClose} width='min(92%, 880px)'>
        <PanelContainer>
          <PanelHeader>
            <div>
              <PanelTitle>{locationName} &ndash; Preferred Providers</PanelTitle>
              {updatedAt && (
                <Subtitle>
                  Last updated on {simpleDateAtTime(updatedAt)}
                  {updatedByName && ` by ${updatedByName}`}
                </Subtitle>
              )}
            </div>
            <CloseButton onClick={requestClose} />
          </PanelHeader>
          <PanelContent>
            {editable && (
              <InputGroup title='Search for providers to add to your preferred providers'>
                <Select<ProviderOption>
                  {...asyncOptions}
                  placeholder='Search for provider to add...'
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => option.name}
                  formatOptionLabel={(option) => (
                    <OptionLabel>
                      <span>{option.name}</span>
                      <OptionType>{option.groupType?.displayName}</OptionType>
                    </OptionLabel>
                  )}
                  value={null}
                  // With no selectable types an empty groupType would match every network group, so offer nothing.
                  disabled={!selectableTypeIds.length}
                  onChange={(option) => option && addProvider(option)}
                />
              </InputGroup>
            )}
            <ItemsCount>
              {count} Preferred Provider{count === 1 ? '' : 's'}
            </ItemsCount>
            <SearchContainer>
              <Search value={search} onChange={setSearch} placeholder='Search' />
            </SearchContainer>
            <DataTable
              columns={columns}
              data={entries}
              defaultSortBy={[{ id: 'name', desc: false }]}
              hasFooter={false}
              loading={loading}
            />
          </PanelContent>
          {editable && (
            <PanelFooter>
              <ButtonGroup>
                <Button
                  onClick={() => apply(rows.map((row) => row.group.id))}
                  disabled={!open || !hasChanges || isApplying}
                  loading={isApplying}>
                  Apply
                </Button>
              </ButtonGroup>
            </PanelFooter>
          )}
        </PanelContainer>
      </Foldout>

      {/* Rendered outside the Foldout: the panel's transform would otherwise make it the containing
        block for the modal's fixed backdrop, centering the confirm inside the panel. Same as
        HighCostMedListSlideout. */}
      {confirmDiscard && (
        <ModalWrapper>
          <DiscardChangesModal
            onCancel={() => setConfirmDiscard(false)}
            onConfirm={() => {
              setConfirmDiscard(false);
              animateClose();
            }}
          />
        </ModalWrapper>
      )}
    </>
  );
}

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 500;
  margin: 0;
`;

const Subtitle = styled.div`
  font-size: 12px;
  color: ${colors.black50};
  margin-top: 4px;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ModalWrapper = styled.div`
  position: relative;
  z-index: 1200;
`;

const PanelFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
  display: flex;
  justify-content: flex-end;
`;

const OptionLabel = styled.div`
  display: flex;
  flex-direction: column;
`;

const OptionType = styled.span`
  font-size: 12px;
  color: ${colors.black50};
`;

const ItemsCount = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.black100};
`;

const SearchContainer = styled.div`
  max-width: 260px;
`;

const AddedOnCell = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const IconButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
`;
